import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { adminAPI } from '@/lib/adminApi';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { AddClientDialog } from '@/components/admin/AddClientDialog';
import { Search, UserPlus, Loader2 } from 'lucide-react';

const ClientRow = ({ c }) => (
  <Link to={`/admin/clients/${c.id}`} data-testid={`dashboard-client-${c.id}`}
    className="group flex items-center gap-3 p-3 rounded-lg bg-[#050816]/40 hover:bg-[#050816]/80 transition-colors">
    <div className="w-9 h-9 shrink-0 rounded-full bg-[#C6A14A]/15 flex items-center justify-center text-[#C6A14A]">
      {(c.full_name || c.email || '?').charAt(0).toUpperCase()}
    </div>
    <div className="min-w-0">
      <p className="text-white truncate group-hover:text-[#C6A14A] transition-colors">{c.full_name || c.email}</p>
      <p className="text-xs text-[#E8E4DE]/50 truncate">{[c.full_name ? c.email : null, c.phone].filter(Boolean).join(' · ') || 'No contact details'}</p>
    </div>
  </Link>
);

export const ClientsPanel = ({ recentClients }) => {
  const [query, setQuery] = useState('');
  const [results, setResults] = useState(null);
  const [searching, setSearching] = useState(false);
  const [addOpen, setAddOpen] = useState(false);

  useEffect(() => {
    const q = query.trim();
    if (q.length < 2) { setResults(null); return undefined; }
    const t = setTimeout(async () => {
      setSearching(true);
      try { setResults((await adminAPI.listUsers(1, 8, q)).users); } catch { setResults([]); }
      setSearching(false);
    }, 300);
    return () => clearTimeout(t);
  }, [query]);

  const list = results ?? recentClients;
  return (
    <section className="rounded-2xl border border-white/10 bg-[#0f1724] p-5" data-testid="dashboard-clients">
      <div className="flex items-center justify-between mb-4 gap-2">
        <h2 className="text-lg text-white font-light tracking-wide">Clients</h2>
        <Button size="sm" onClick={() => setAddOpen(true)} className="bg-[#C6A14A] text-[#050816] hover:bg-[#D4AF5A]" data-testid="dashboard-add-client-btn">
          <UserPlus className="h-4 w-4 mr-1" /> Add Client
        </Button>
      </div>
      <div className="relative mb-3">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-[#E8E4DE]/40" />
        <Input value={query} onChange={(e) => setQuery(e.target.value)} placeholder="Search name, email or phone"
          className="pl-9 bg-[#050816]/50 border-white/10 text-white placeholder:text-white/30" data-testid="dashboard-client-search" />
        {searching && <Loader2 className="absolute right-3 top-1/2 -translate-y-1/2 h-4 w-4 animate-spin text-[#C6A14A]" />}
      </div>
      <p className="text-xs uppercase tracking-[0.15em] text-[#E8E4DE]/50 mb-2">{results ? 'Search results' : 'Recent clients'}</p>
      <div className="space-y-2" data-testid="dashboard-client-list">
        {list.length ? list.map((c) => <ClientRow key={c.id} c={c} />)
          : <p className="text-sm text-[#E8E4DE]/40 py-4" data-testid="dashboard-client-empty">{results ? 'No clients match' : 'No clients yet'}</p>}
      </div>
      <Link to="/admin/clients" className="block text-sm text-[#C6A14A] hover:underline mt-4" data-testid="admin-hub-clients-link">All clients →</Link>
      <AddClientDialog open={addOpen} onOpenChange={setAddOpen} />
    </section>
  );
};
